/**
 * "Your photos are locked" — and a way out that is not signing out.
 *
 * The keyring lives in memory and in this device's IndexedDB, and neither
 * survives every circumstance: a different browser, cleared site data, a private
 * window, a second computer. What was there before was a grid of padlocks, no
 * explanation, and one remedy nobody would guess — sign out and sign in again,
 * losing your place, to fix something that has nothing to do with the session.
 *
 * Two things are wrong with a padlock on its own. It reads as *"you are not
 * allowed"*, when the truth is "this browser cannot open these yet". And it
 * offers nothing to do about it, so the reasonable conclusion is that the
 * photographs are gone.
 *
 * The password is asked for because it is genuinely needed: the private key is
 * sealed under a key derived from it, and nothing else on the device or the
 * server can open it. That is worth one sentence on screen, or it looks like an
 * app being suspicious of somebody who is already signed in.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useT } from "../../lib/i18n.js";
import { useSyncExternalStore } from "react";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { useAuth } from "../../lib/auth.js";
import { isUnlocked, subscribeKeyring } from "../../lib/keyring.js";
import "./UnlockBar.css";

export function UnlockBar() {
  const t = useT();
  const { status, user, unlockKeyring } = useAuth();
  const unlocked = useSyncExternalStore(
    subscribeKeyring,
    isUnlocked,
    () => true,
  );

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nothing to say to somebody who is not signed in, or whose keyring is fine.
  if (status !== "authenticated" || unlocked) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const opened = await unlockKeyring(password, totpCode || undefined);
      if (opened) {
        setOpen(false);
        setPassword("");
        setTotpCode("");
      } else {
        setError(
          "That did not open the keyring. Check the password and try again.",
        );
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fc-unlock" role="status">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 10.5h12v9H6zM8.5 10.5V7a3.5 3.5 0 1 1 7 0v3.5" />
        </svg>
        <p>
          <strong>Your photos are locked on this browser.</strong> They are safe
          — this device just cannot open them until you enter your password
          again.
        </p>
        <Button size="sm" variant="primary" onClick={() => setOpen(true)}>
          {t("Unlock")}
        </Button>
      </div>

      <Dialog
        open={open}
        title={t("Unlock your photos")}
        onClose={() => setOpen(false)}
      >
        <form
          className="fc-unlock__form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <p className="fc-unlock__why">
            {t("Your photos are encrypted. Unlock them with your password, use a saved recovery file, or ask a trusted group member to help.")}
          </p>

          <Field label={t("Password")} required error={error ?? undefined}>
            <Input
              type="password"
              value={password}
              autoFocus
              autoComplete="current-password"
              disabled={busy}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          {user?.totpEnabled === true && (
            <Field label={t("Authentication code")} required>
              <Input
                value={totpCode}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                disabled={busy}
                onChange={(event) => setTotpCode(event.target.value)}
              />
            </Field>
          )}

          <Link to="/recover" onClick={() => setOpen(false)}>{t("Forgot your password?")}</Link>

          <div className="fc-unlock__actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setOpen(false)}
            >
              Not now
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={busy}
              disabled={!password}
            >
              {t("Unlock")}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
